// Inside PDCAKanban.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  IconButton,
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import CreatePDCAButton from "../buttons/CreatePDCAButton";
import PDCAFilter from "../data/PDCAFilter";
import initialTasks from "../data/PDCAData";
import CloseIcon from "@mui/icons-material/Close"; // Import CloseIcon

const columns = ["Plan", "Do", "Check", "Act"];
const columnColors = {
  Plan: "#e3f2fd",
  Do: "#e8f5e9",
  Check: "#fff3e0",
  Act: "#f3e5f5",
};

export default function PDCAKanban() {
  const [tasks, setTasks] = useState(initialTasks);
  const [filters, setFilters] = useState({ owner: "All" });
  const [selectedTask, setSelectedTask] = useState(null); // State for selected task
  const [selectedColumn, setSelectedColumn] = useState(null); // State for selected column

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleCardClick = (task, column) => {
    setSelectedTask(task); // Set the clicked task as the selected task
    setSelectedColumn(column); // Set the column of the clicked task
  };

  const handleCloseDetails = () => {
    setSelectedTask(null); // Clear the selected task
    setSelectedColumn(null); // Clear the selected column
  };

  const filteredTasks = Object.keys(tasks).reduce((acc, column) => {
    acc[column] = tasks[column].filter(
      (task) => filters.owner === "All" || task.owner === filters.owner
    );
    return acc;
  }, {});

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceCol = source.droppableId;
    const destCol = destination.droppableId;

    const sourceTasks = Array.from(tasks[sourceCol]);
    const destTasks = Array.from(tasks[destCol]);
    const [movedTask] = sourceTasks.splice(source.index, 1);

    if (sourceCol === destCol) {
      sourceTasks.splice(destination.index, 0, movedTask);
      setTasks({ ...tasks, [sourceCol]: sourceTasks });
    } else {
      destTasks.splice(destination.index, 0, movedTask);
      setTasks({
        ...tasks,
        [sourceCol]: sourceTasks,
        [destCol]: destTasks,
      });
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 2,
        overflowX: "hidden", // Prevent horizontal scrolling
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">PDCA Kanban Board</Typography>
        <Stack direction="row" spacing={2}>
          <PDCAFilter filters={filters} onFilterChange={handleFilterChange} />
          <CreatePDCAButton onClick={() => alert("PDCA Creation Modal")} />
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row", // Arrange Kanban and detail view side by side
          gap: 3,
        }}
      >
        {/* Kanban Board */}
        <DragDropContext onDragEnd={onDragEnd}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap", // Allow wrapping for smaller screens
              gap: 3,
              overflowX: "auto",
              pb: 2,
              pt: 1,
              justifyContent: "center", // Center columns on smaller screens
              flex: selectedTask ? 1 : 2, // Adjust space when Task Details is open
            }}
          >
            {(selectedColumn ? [selectedColumn] : columns).map((column) => (
              <Paper
                key={column}
                elevation={3}
                sx={{
                  flex: "1 1 300px", // Adjust width dynamically
                  maxWidth: 380,
                  minWidth: 280, // Minimum width for smaller screens
                  p: 2,
                  bgcolor: "#fff",
                  border: "1px solid #d3d3d3", // Add light gray border
                  borderRadius: "4px", // Optional: keep consistent border radius
                }}
              >
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{
                    backgroundColor: columnColors[column],
                    padding: "8px",
                    borderRadius: "4px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  {column}
                </Typography>

                <Droppable droppableId={column}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        minHeight: "120px",
                        backgroundColor: snapshot.isDraggingOver
                          ? "#f0f0f0"
                          : "transparent",
                        padding: 1,
                        borderRadius: 1,
                        transition: "background-color 0.3s",
                      }}
                    >
                      {filteredTasks[column].map((task, index) => (
                        <Draggable
                          key={task.id}
                          draggableId={task.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={{
                                mb: 2,
                                backgroundColor: snapshot.isDragging
                                  ? "#bbdefb"
                                  : "#fff",
                                boxShadow: snapshot.isDragging ? 4 : 1,
                                cursor: "pointer", // Indicate clickable cards
                              }}
                              onClick={() => handleCardClick(task, column)} // Handle card click
                            >
                              <CardContent>
                                <Typography variant="body2">
                                  {task.title}
                                </Typography>
                                <Chip
                                  label={task.owner}
                                  size="small"
                                  sx={{ mt: 1 }}
                                />
                              </CardContent>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Paper>
            ))}
          </Box>
        </DragDropContext>

        {/* Detailed View */}
        {selectedTask && (
          <Box
            sx={{
              flex: 1.5, // Increase space allocation for the detailed view
              p: 2,
              border: "1px solid #d3d3d3",
              borderRadius: "4px",
              bgcolor: "#f9f9f9",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">Task Details</Typography>
              <IconButton onClick={handleCloseDetails} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1">Title:</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedTask.title}
            </Typography>
            <Typography variant="subtitle1">Owner:</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedTask.owner}
            </Typography>
            <Typography variant="subtitle1">Description:</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedTask.description || "No description available."}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
